<template>
  <div
    ref="episodes"
    :class="{dark: isDark, 'small-text': smallText}"
    aria-label="Episode Playlist"
    class="episodes"
  >
    <div
      :style="tableStyles"
      class="episodes-list"
    >
      <button
        v-for="(epi, key) in episodes"
        :class="{selected: epi.enclosure_url === episode.enclosure_url}"
        :key="key"
        :aria-label="playEpisodeTitle(epi)"
        class="episodes-list_item"
        @click="playEpisode(epi)"
      >
        <span class="number">{{ getEpisodeNumber(epi) }}</span>
        <span class="title"><div class="marquee">{{ epi.title }}</div></span>
        <span class="duration">
          <IconSpeaker v-if="epi.enclosure_url === episode.enclosure_url && isPlaying" />
          <template v-else>
            {{ time(epi.duration) }}
          </template>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import IconSpeaker from '../assets/svg/speaker.svg'
import Time from '../helpers/Time'

export default {
  components: {
    IconSpeaker
  },
  props: {
    store: {
      type: Object,
      default: function () {}
    }
  },
  data () {
    return {
      innerHeight: 0,
      innerWidth: 0,
      offsetHeight: 0,
      offsetWidth: 0
    }
  },
  computed: {
    smallText () {
      return this.offsetWidth < 350
    },
    audio () { return this.store.state.audio },
    currentTime () { return this.store.state.currentTime },
    episode () {
      return this.store.state.episode
    },
    episodes () {
      try {
        return this.store.state.podcast.episodes.collection
      } catch (e) {
        return []
      }
    },
    isDark () { return this.store.state.isDark },
    isPlaying () { return this.store.state.isPlaying },
    tableStyles () {
      return {
        background: this.store.state.color
      }
    }
  },
  watch: {
    currentTime (val) {
      if (val >= this.audio.duration) {
        this.playNextEpisode()
      }
    }
  },
  created () {
    window.addEventListener('resize', this.setOffsets)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.setOffsets)
  },
  mounted () {
    this.setOffsets()
    // browser needs a little help
    setTimeout(this.setOffsets, 100)
  },
  methods: {
    setOffsets () {
      const style = window.getComputedStyle(this.$refs.episodes, null)
      const height = parseInt(style.getPropertyValue('height').replace('px', ''))
      const padding = parseInt(style.getPropertyValue('padding').replace('px', '')) || 0
      const border = parseInt(style.getPropertyValue('border-width').replace('px', '')) || 0
      this.innerHeight = height - (padding * 2) - (border * 2)
      this.offsetWidth = this.$refs.episodes.offsetWidth + (padding)
      this.offsetHeight = this.$refs.episodes.offsetHeight
    },
    getEpisodeNumber (episode) {
      if (!episode || !this.store.state.podcast) return ''
      const hasEpNum = typeof this.episode.number === 'number' && this.episode.number > -1
      const season = this.store.state.podcast.type === 'episodic' ? '' : `S${episode.season_number}:`
      const type = episode.type.charAt(0).toUpperCase()
      if (!hasEpNum && episode.type === 'full') {
        return season.length > 0 ? season : '-'
      }
      const ep = episode.type === 'full' ? `E${episode.number}` : type
      return season + ep
    },
    playEpisodeTitle (episode) {
      if (episode.id === this.episode.id && this.store.state.isPlaying) {
        return `Pause Episode: ${episode.title}`
      }
      return `Play Episode: ${episode.title}`
    },
    playEpisode (episode) {
      if (episode.id === this.episode.id) {
        if (this.store.state.isPlaying) {
          this.audio.pause()
        } else {
          this.audio.play()
        }
      } else {
        // If playEpisode gets triggered this means that the playlist player was already playing
        // since the audio.play() won't trigger a new event we need to trigger this
        if (this.store.state.isPlaying) {
          this.store.sendPlayEvent()
          this.store.state.playStart = 0
        }

        this.store.setEpisode(episode)
        this.store.getEpisodeWaveform(episode)
        this.audio.play()
      }
    },
    playNextEpisode () {
      for (let i = 0; i < this.episodes.length; i += 1) {
        let episode = this.episodes[i]

        if (episode.id === this.episode.id && this.episodes[i + 1]) {
          this.playEpisode(this.episodes[i + 1])
          this.audio.play()
          this.store.setPlaybackRate(this.store.state.currentRate)
          break
        }
      }
    },
    time (time) {
      return Time.iso(time || 0)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'node_modules/simplecast-styles/src/assets/styles/_core/colors.scss';
@import 'node_modules/simplecast-styles/src/assets/styles/_core/fonts.scss';

.episodes {
  background: $black-trans-100;
  border: solid 1px $black-trans-200;
  border-radius: 2px;
  flex: 1;
  overflow-y: scroll;

  &.dark {
    background: $white-trans-200;
    border-color: $white-trans-300;

    .episodes-list {
      &_item {
        &.selected {
          background: $white-trans-300;
        }
        &:hover {
          background: $white-trans-200;
        }
        &:focus {
          background: $white-trans-300;
        }
        border-bottom: solid 1px $white-trans-200;

      }
    }
    .duration,
    .number {
      color: $white-trans-800;
    }
    .duration svg {
      fill:  $white-trans-800;
    }
    .title {
      color: $white-trans-800;
    }
  }
  .episodes-list {
    background: $white-000;
    border-spacing: 0;
    max-width: 100%;
  }
  .episodes-list_item {
    cursor: pointer;
    background: transparent;
    border: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-height: 28px;

    &:hover {
      background: $black-trans-200;
    }
    &.selected {
        background: $black-trans-100;
    }
    &:focus {
      background: $black-trans-300;
      outline: 0;
    }
    border-bottom: solid 1px $black-trans-200;
    padding: 12px 0;

    span {
      display: block;
    }
  }

  .number {
    color: $black-trans-800;
    padding-left: 16px;
    width: 92px;
    padding-right: 16px;
    text-align: left;
    @include font-s();
    box-sizing: border-box;
  }
  .title {
    text-align: left;
    flex-grow: 1;
    width: calc(100% - 194px);
    overflow-x: hidden;
    box-sizing: border-box;
    @include font-m-embed(700);
    line-height: 1.2 !important;
    .marquee {
      white-space: nowrap;
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .duration {
    height: 23px;
    max-width: 100px;
    color: $black-trans-800;
    text-align: right;
    width: 100px;
    padding: 0 16px;
    @include font-s();
    box-sizing: border-box;

    svg {
      height: 18px;
      position: relative;
      top: 2px;
      width: 18px;
      fill: $black-trans-800;
    }
  }
  &.small-text {
     .number, .duration {
       font-size: 10px !important;
       padding: 0 8px;
       width: 80px;
     }
     .title {
       font-size: 11px !important;
     }
     .duration {
       width: 100px;
     }
  }
}
</style>
